import { useMemo } from "react";

import { AnimatePresence, motion } from "framer-motion";

import cardLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";

import { PacingStatus } from "../../../shared/pacing-status";
import { useBudgetedKPIToDate } from "../kpi-tile";
import { useBudgetedSpendToDate } from "../spend-tile";

import { DerivedKpiTileChart } from "./derived-kpi-chart";

type Props = {
  spend?: {
    actual: number;
    budgeted: number;
    target: number;
  };
  kpi?: {
    name: string;
    actual: number;
    budgeted: number;
    target: number;
    predictedRange: {
      low: number;
      median: number;
      high: number;
    };
  };
};

export const DerivedKpiTile = ({ spend, kpi }: Props) => {
  const {
    formattedMixToDateRange,
    formattedCycleDateRange,
    efficiencyKpiType,
    efficiencyKpiFormatter,
    efficiencyKpiCalculator,
    percentOfCycleCompleted,
  } = useClientSettings();

  const { budgetedKPIToDate, loading: kpiLoading } = useBudgetedKPIToDate();
  const { adoptedBudgetedSpendToDate, loading: spendLoading } =
    useBudgetedSpendToDate();

  const isReady =
    spend &&
    kpi?.predictedRange &&
    efficiencyKpiType &&
    !(kpiLoading || spendLoading);

  const derivedActual = useMemo(
    () => (isReady ? efficiencyKpiCalculator(spend.actual, kpi.actual) : 0),
    [isReady, spend, kpi, efficiencyKpiCalculator],
  );

  const derivedBudgeted = useMemo(
    () =>
      isReady
        ? efficiencyKpiCalculator(adoptedBudgetedSpendToDate, budgetedKPIToDate)
        : 0,
    [
      isReady,
      efficiencyKpiCalculator,
      adoptedBudgetedSpendToDate,
      budgetedKPIToDate,
    ],
  );

  const derivedTargetLow = useMemo(
    () =>
      isReady
        ? efficiencyKpiCalculator(spend.budgeted, kpi.predictedRange.low)
        : 0,
    [isReady, spend?.budgeted, kpi, efficiencyKpiCalculator],
  );

  const derivedTargetMedian = useMemo(
    () =>
      isReady
        ? efficiencyKpiCalculator(spend.budgeted, kpi.predictedRange.median)
        : 0,
    [isReady, spend?.budgeted, kpi, efficiencyKpiCalculator],
  );

  const derivedTargetHigh = useMemo(
    () =>
      isReady
        ? efficiencyKpiCalculator(spend.budgeted, kpi.predictedRange.high)
        : 0,
    [isReady, spend?.budgeted, kpi, efficiencyKpiCalculator],
  );

  if (!isReady) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="border border-v2-secondary rounded w-full h-[270px] flex-center"
        >
          <img className="h-1/2" src={cardLoading} alt="Loading" />
        </motion.div>
      </AnimatePresence>
    );
  }

  const isCompleted = percentOfCycleCompleted === 1;

  return (
    <motion.div className="border border-v2-secondary rounded p-4 w-full min-w-0">
      <div className="flex justify-between mb-3">
        <div className="font-semibold text-xl truncate flex-1 mr-2 flex-grow">
          {efficiencyKpiType}
        </div>
        <PacingStatus
          className="rounded px-3 h-7 font-semibold text-xs"
          prefix="Pacing"
          actual={derivedActual}
          budgeted={derivedBudgeted}
          subject="forecast"
          isNegativeTrendGood={efficiencyKpiType === "CAC"}
        />
      </div>
      <div className="text-xs tracking-wide relative flex items-center justify-between">
        <div className="bg-cosmic opacity-10 rounded-t absolute h-7 w-full"></div>
        <div className="py-1.5 pl-2 flex items-center space-x-2 font-semibold tracking-wide">
          MIX {isCompleted ? "COMPLETED" : "TO DATE"}
        </div>
        <div className="py-1.5 pr-2 text-gray+2">{formattedMixToDateRange}</div>
      </div>
      <div className="h-36 bg-v2-secondary px-2.5">
        <DerivedKpiTileChart
          formatter={efficiencyKpiFormatter}
          dateRangeText={formattedMixToDateRange}
          derivedActual={derivedActual}
          derivedBudgeted={derivedBudgeted}
        />
      </div>
      <div className="flex justify-between items-center mt-2">
        <div className="flex flex-col text-xs">
          <div className="font-semibold tracking-wide">TOTAL PERIOD TARGET</div>
          <div className="text-gray+2">{formattedCycleDateRange}</div>
        </div>
        <div className="min-w-[164px] text-xl">
          <div className="flex items-center space-x-1 w-full">
            <div className="text-xs self-center text-interstellar">
              {efficiencyKpiFormatter(derivedTargetLow, "approximate")}
            </div>
            <div
              className="flex-1 h-1 rounded-full"
              style={{
                background: "linear-gradient(90deg, #F05F33 0%, #FFD247 100%)",
              }}
            />
            <div className="font-semibold">
              {efficiencyKpiFormatter(derivedTargetMedian, "approximate")}
            </div>
            <div
              className="flex-1 h-1 rounded-full"
              style={{
                background: "linear-gradient(90deg, #FFD247 0%, #50BA58 100%)",
              }}
            />
            <div className="shrink-0 text-xs self-center text-interstellar">
              {efficiencyKpiFormatter(derivedTargetHigh, "approximate")}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
